'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { formatIndianNumber } from '../lib/format-number'

/* Footer link → Module Control flag key. A link is hidden when its module is
   switched OFF (and its route is independently 404'd by middleware). Links absent
   from this map (About, Contact, Privacy, Terms, MasjidOS, FAQ…) always show. */
const HREF_FLAG: Record<string, string> = {
  '/masjids': 'masjidDirectory',
  '/feed': 'feed',
  '/announcements': 'announcements',
  '/ramadan': 'ramadanModeLive',
  '/waqf': 'waqfGuardian',
  '/onboard': 'addListMasjid',
  '/masjid/claim': 'addListMasjid',
  '/give?tab=campaigns': 'campaigns',
  '/give': 'donations',
  '/volunteers': 'volunteerNetwork',
  '/leaderboard': 'leaderboard',
  '/jobs': 'jobsPortal',
  '/business': 'communityPlus',
  '/connect': 'masjidConnect',
  '/blog': 'blog',
  '/islamic-calendar': 'islamicCalendar',
  '/search': 'search',
  '/pricing': 'pricingPage',
}

function FooterLogoImg() {
  const DEFAULT = '/homepage-assets/brand-logo-footer.png'
  const [src, setSrc] = useState(DEFAULT)
  useEffect(() => {
    const load = () => {
      try {
        const saved = localStorage.getItem('masjids_foot_logo')
        setSrc(saved || DEFAULT)
      } catch {}
    }
    load()
    window.addEventListener('logoUpdated', load)
    return () => window.removeEventListener('logoUpdated', load)
  }, [])
  // the default footer asset is already white+gold; only invert custom/legacy logos
  const isDefault = src === DEFAULT
  return <img src={src} alt="Masjids.in" style={{ height: 54, width: 'auto', display: 'block', filter: isDefault ? undefined : 'brightness(0) invert(1)' }} onError={() => setSrc(DEFAULT)} />
}

function isProtectedFooterHref(href: string) {
  return href.startsWith('/admin') || href.startsWith('/dashboard') || href.startsWith('/moderator') || href.startsWith('/volunteer/dashboard')
}

type FooterStats = { masjids: number; cities: number; verified: number; campaigns: number }

export default function Footer({ initialStats, initialFlags = {} }: { initialStats?: FooterStats | null; initialFlags?: Record<string, boolean> }) {
  const { t } = useLanguage()
  /* SSR-correct flag gating: link visibility from the SERVER-resolved flag map threaded from the
     root layout, not the post-mount client hook — so an OFF module's footer link is absent from
     the initial HTML on every route. `!== false` keeps fail-open (missing key → shown). */
  const flagOn = (k?: string) => !k || initialFlags[k] !== false

  /* SSR-fed from the layout (stats engine) — no hardcoded snapshot. A client
     refetch keeps long-lived tabs current. */
  const [stats, setStats] = useState<FooterStats | null>(initialStats || null)
  useEffect(() => {
    let alive = true
    fetch('/api/stats')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d?.masjids) setStats(d) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const COLS = [
    { title: t('Platform'), links: [
      [t('Find Masjids'), '/masjids'],
      [t('Live Ummah Feed'), '/feed'],
      [t('Announcements & Notices'), '/announcements'],
      [t('Ramadan'), '/ramadan'],
      [t('Masjid Waqf Suite'), '/waqf'],
    ] },
    { title: t('For Masjids'), links: [
      [t('Register Masjid'), '/onboard'],
      [t('Claim Masjid'), '/masjid/claim'],
      ['Masjid OS', '/dashboard'],
      [t('Announcements'), '/announcements'],
      [t('Donation Campaigns'), '/give?tab=campaigns'],
    ] },
    { title: t('Community'), links: [
      [t('Give'), '/give'],
      [t('Volunteer'), '/volunteers'],
      [t('Jobs'), '/jobs'],
      [t('Community+'), '/business'],
      [t('Masjid Connect'), '/connect'],
    ] },
    { title: t('Resources'), links: [
      [t('Blog'), '/blog'],
      [t('Islamic Calendar'), '/islamic-calendar'],
      [t('Pricing'), '/pricing'],
      [t('Advertise'), '/advertise'],
      [t('Contact'), '/contact'],
      [t('FAQ'), '/faq'],
      [t('Search'), '/search'],
    ] },
    { title: t('Company'), links: [
      [t('About Us'), '/about'],
      [t('Contact'), '/contact'],
      [t('Pricing'), '/pricing'],
      [t('Privacy Policy'), '/privacy'],
      [t('Terms of Service'), '/terms'],
    ] },
  ]

  return (
    <footer className="site-footer">
      <div className="container site-footer-inner">
        <section className="site-footer-cta" aria-label="Masjids.in ecosystem closing statement">
          <div>
            <span className="site-footer-eyebrow">{t('Muslim civic infrastructure')}</span>
            <h2>{t("Built for India's 200 million Muslims.")}</h2>
            <p>{t("India's AI-powered masjid ecosystem for prayer, giving, waqf protection, community action, and trusted institutional life.")}</p>
          </div>
          {stats && <div className="site-footer-metrics" aria-label="Live ecosystem metrics">
            {[
              [formatIndianNumber(stats.masjids), t('Masjids listed')],
              [String(stats.cities), t('Cities')],
              [String(stats.verified), t('Verified')],
              [String(stats.campaigns), t('Live campaigns')],
            ].map(([value, label]) => (
              <div key={label}>
                <strong>{value}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>}
        </section>

        <div className="site-footer-grid">
          <div className="site-footer-brand">
            <div className="site-footer-logo"><FooterLogoImg /></div>
            <p>{t('Find, fund, operate, and protect Muslim institutions through one calm civic operating layer.')}</p>
          </div>

          {COLS
            .map((col) => ({ ...col, links: col.links.filter(([, href]) => flagOn(HREF_FLAG[href as string])) }))
            .filter((col) => col.links.length > 0)
            .map((col) => (
            <nav key={col.title} className="site-footer-links" aria-label={col.title}>
              <h3>{col.title}</h3>
              {col.links.map(([label, href]) => (
                <Link key={href} href={href} prefetch={isProtectedFooterHref(href) ? false : undefined}>{label}</Link>
              ))}
            </nav>
          ))}
        </div>

        <div className="site-footer-bottom">
          <div>&copy; 2025 Masjids.in - {t('Digital Ecosystem for the Ummah')}</div>
          <div className="site-footer-signal"><span /> {t('Live civic ecosystem')}</div>
        </div>

        {/* Fine-print legal line — OpenStreetMap/ODbL attribution is a LICENSE REQUIREMENT and
            must remain; kept as an unobtrusive muted footnote at the very bottom with both
            hyperlinks intact. */}
        <div className="site-footer-legal">
          Masjid location data &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors, available under the <a href="https://opendatacommons.org/licenses/odbl/" target="_blank" rel="noopener noreferrer">ODbL</a>
        </div>
      </div>

      <style jsx>{`
        .site-footer {
          position: relative;
          overflow: hidden;
          /* Clean Navy base (#0B132B), single vertical gradient to a slightly
             darker navy. ONE subtle emerald accent at top-center only — no gold,
             no diagonal, no competing glows (the old green+gold-over-diagonal mix
             blended to a muddy brown-olive wash). */
          background:
            radial-gradient(ellipse 78% 56% at 50% -8%, rgba(14,124,97,0.16), transparent 70%),
            linear-gradient(180deg, #0B132B 0%, #070D1C 100%);
          color: white;
          padding: 18px 0 16px;
        }
        .site-footer::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          opacity: 0.12;
          background-image:
            linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px);
          background-size: 42px 42px;
          mask-image: linear-gradient(180deg, black, transparent 82%);
        }
        .site-footer-inner {
          position: relative;
          z-index: 1;
        }
        .site-footer-cta {
          display: none;
          grid-template-columns: minmax(0, 1fr) minmax(280px, 0.82fr);
          gap: 24px;
          align-items: center;
          margin-bottom: 28px;
          padding: 22px;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 24px;
          background: rgba(255,255,255,0.06);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.12), 0 22px 60px rgba(0,0,0,0.18);
          backdrop-filter: blur(18px);
        }
        .site-footer-eyebrow {
          display: inline-flex;
          margin-bottom: 8px;
          color: #f5d577;
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .site-footer-cta h2 {
          max-width: 720px;
          margin: 0;
          font-family: var(--font-display, Georgia, serif);
          font-size: clamp(1.8rem, 3.1vw, 3.1rem);
          line-height: 1.04;
          letter-spacing: 0;
        }
        .site-footer-cta p {
          max-width: 720px;
          margin: 10px 0 0;
          color: rgba(255,255,255,0.7);
          font-size: 14px;
          line-height: 1.7;
        }
        .site-footer-metrics {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }
        .site-footer-metrics div {
          min-width: 0;
          padding: 14px;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 18px;
          background: rgba(255,255,255,0.08);
        }
        .site-footer-metrics strong {
          display: block;
          font-size: 24px;
          line-height: 1;
        }
        .site-footer-metrics span {
          display: block;
          margin-top: 6px;
          color: rgba(255,255,255,0.58);
          font-size: 11px;
          font-weight: 800;
        }
        .site-footer-grid {
          display: grid;
          grid-template-columns: minmax(220px, 1fr) repeat(5, minmax(112px, 0.54fr));
          gap: 16px;
          margin-bottom: 12px;
        }
        .site-footer-logo img {
          height: 40px !important;
        }
        .site-footer-brand p {
          max-width: 270px;
          margin: 9px 0;
          color: rgba(255,255,255,0.72);
          font-size: 11px;
          line-height: 1.6;
        }
        .site-footer-colors {
          display: flex;
          align-items: center;
          gap: 7px;
          margin-bottom: 7px;
        }
        .site-footer-colors span {
          width: 10px;
          height: 10px;
          border-radius: 999px;
        }
        .site-footer-colors em,
        .site-footer-brand small {
          color: rgba(255,255,255,0.62);
          font-size: 11px;
          font-style: normal;
        }
        .site-footer-links {
          display: grid;
          align-content: start;
          gap: 7px;
          /* footer columns are <nav> elements — keep them FLAT on the footer
             background: no fill/border/box from the global nav scaffold or
             anywhere else (the global nav rule was painting each column navy,
             reading as a lighter raised panel against the gradient). */
          background: none;
          border: none;
          box-shadow: none;
          padding: 0;
          min-height: 0;
        }
        .site-footer-links h3 {
          margin: 0 0 3px;
          color: rgba(255,255,255,0.7);
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .site-footer-links a {
          color: rgba(255,255,255,0.78);
          font-size: 12px;
          text-decoration: none;
          transition: color var(--motion-fast, 150ms ease);
        }
        .site-footer-links a:hover {
          color: white;
        }
        .site-footer-links a:nth-of-type(n+4) {
          display: none;
        }
        .site-footer-bottom {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          gap: 10px;
          padding-top: 13px;
          border-top: 1px solid rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.62);
          font-size: 11px;
        }
        .site-footer-legal {
          margin-top: 8px;
          padding-top: 8px;
          text-align: center;
          color: rgba(255,255,255,0.34);
          font-size: 10px;
          line-height: 1.5;
        }
        .site-footer-legal a {
          color: rgba(255,255,255,0.5);
          text-decoration: underline;
        }
        .site-footer-signal {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .site-footer-signal span {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #14b8a6;
          box-shadow: 0 0 0 6px rgba(20,184,166,0.14);
        }

        .site-footer-cta {
          position: relative;
          overflow: hidden;
        }

        .site-footer-cta::before {
          content: "";
          position: absolute;
          right: 16px;
          bottom: 12px;
          width: min(420px, 54%);
          height: 72%;
          pointer-events: none;
          opacity: 0.24;
          background:
            radial-gradient(circle at 18% 34%, rgba(20,184,166,0.72) 0 4px, transparent 5px),
            radial-gradient(circle at 58% 22%, rgba(245,213,119,0.68) 0 4px, transparent 5px),
            radial-gradient(circle at 78% 66%, rgba(20,184,166,0.62) 0 4px, transparent 5px),
            linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px);
          background-size: auto, auto, auto, 36px 36px, 36px 36px;
          mask-image: radial-gradient(ellipse at center, #000 0%, transparent 72%);
          animation: footer-map-drift 8s ease-in-out infinite;
        }

        .site-footer-cta::after {
          content: "";
          position: absolute;
          left: 22px;
          right: 22px;
          bottom: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(245,213,119,0.46), rgba(20,184,166,0.36), transparent);
        }

        .site-footer-cta > * {
          position: relative;
          z-index: 1;
        }

        .site-footer-metrics div {
          position: relative;
          overflow: hidden;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.1);
        }

        .site-footer-metrics div:nth-child(1),
        .site-footer-metrics div:nth-child(4) {
          background:
            radial-gradient(circle at 100% 0%, rgba(20,184,166,0.2), transparent 32%),
            rgba(255,255,255,0.08);
        }

        .site-footer-metrics div:nth-child(2) {
          background:
            radial-gradient(circle at 100% 0%, rgba(245,213,119,0.2), transparent 32%),
            rgba(255,255,255,0.08);
        }

        .site-footer-signal span {
          animation: footer-live-pulse 3s ease-in-out infinite;
        }

        @keyframes footer-live-pulse {
          0%, 100% { transform: scale(1); opacity: 0.76; }
          50% { transform: scale(1.18); opacity: 1; }
        }

        @keyframes footer-map-drift {
          0%, 100% { transform: translate3d(0, 0, 0); opacity: 0.2; }
          50% { transform: translate3d(-8px, -6px, 0); opacity: 0.32; }
        }
        @media (max-width: 1080px) {
          .site-footer-grid {
            grid-template-columns: minmax(220px, 1fr) repeat(3, minmax(140px, 1fr));
          }
        }
        @media (max-width: 900px) {
          .site-footer-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .site-footer-brand {
            grid-column: 1 / -1;
          }
          .site-footer {
            padding-bottom: 72px;
          }
        }
        @media (max-width: 520px) {
          .site-footer {
            padding-top: 22px;
          }
          .site-footer-cta {
            padding: 18px;
            border-radius: 20px;
          }
          .site-footer-metrics {
            grid-template-columns: 1fr 1fr;
          }
          .site-footer-metrics div {
            padding: 12px;
          }
        }
      `}</style>
    </footer>
  )
}
